import { cx } from '../cx';

/**
 * The OFMOS logotype, set in type rather than shipped as a bitmap.
 *
 * Source: docs/brand/ofmos-logo.png - a heavy "OFM" with a small "OS" raised to
 * the top right in the brand cyan. DESIGN.md's brand section calls the
 * superscript-OS "the recognizable move", and it is: without it the mark is
 * three anonymous capitals.
 *
 * WHY IT IS TYPE AND NOT THE PNG. The source is a 640px raster on an opaque
 * black square. Scaling it into a 15px sidebar header would soften it, the
 * black square would fight every surface it lands on, and it would need a
 * second asset for dark mode. Set as text it inherits the surrounding font
 * size, stays sharp at any resolution, recolours itself per theme, and is
 * selectable and searchable. The one thing it gives up is the exact letterform
 * of the original file, which is a licensing question nobody has answered - and
 * a wordmark that is a bitmap is a wordmark that cannot be used inline.
 *
 * SIZING: the mark is 1em, so a caller sets it with a type token
 * (`<Wordmark className="text-md" />`) instead of with a size prop. Everything
 * inside is proportional to that em, which is why the numbers below are em
 * ratios rather than pixels.
 *
 * ON THE INLINE STYLE: these three ratios are the CONSTRUCTION OF THE MARK, in
 * the same sense that the `d` attributes in ui/Icon are the construction of a
 * glyph - not design tokens that another component could ever want to read.
 * They are values of the logo, and they live with it.
 */

/** Cap height of the UI faces is ~0.72em, so half of it re-tops the small OS. */
const SUPER = {
  fontSize: '0.5em',
  /** In the OS's own em, so 0.68 x 0.5 = 0.34em of the mark. */
  bottom: '0.68em',
  marginLeft: '0.1em',
} as const;

export interface WordmarkProps {
  /**
   * Renders the mark in a single colour instead of cyan-on-ink. For places
   * where the mark sits on the accent itself or inside a monochrome asset.
   */
  readonly monochrome?: boolean | undefined;
  readonly className?: string | undefined;
}

export function Wordmark({ monochrome = false, className }: WordmarkProps) {
  return (
    <span
      /**
       * A logotype IS an image that happens to be made of letters. Without
       * this a screen reader meets two separate runs and announces "OFM" then
       * "OS"; with it the mark has one name. The letters stay real text
       * underneath, so find-in-page and copy still work.
       */
      role="img"
      aria-label="OFMOS"
      className={cx(
        'inline-block font-bold tracking-tight whitespace-nowrap text-ink select-none',
        className,
      )}
    >
      OFM
      <span
        className={cx('relative font-bold', monochrome ? 'text-current' : 'text-accent')}
        style={SUPER}
      >
        OS
      </span>
    </span>
  );
}
